// Custom dropdown — the app's standard NON-native control.
// HARD RULE (Gery): never a native <select>/OS dropdown; always this `.dd`-style
// custom control. Inline-expanding (NOT absolute) so it isn't clipped inside a
// scrollable bottom-sheet. Long lists get a search field (shows ~5, filters as you type).
type Opt = { value: string; label: string };

let globalBound = false;
function bindGlobalClose(): void {
  if (globalBound) return;
  globalBound = true;
  document.addEventListener("pointerdown", (e) => {
    const t = e.target as Element | null;
    document.querySelectorAll(".rp-dd.open").forEach((dd) => { if (!dd.contains(t)) dd.classList.remove("open"); });
  });
}

export function makeDropdown(o: {
  options: Opt[];
  value?: string;
  placeholder: string;
  searchable?: boolean;
  onChange?: (value: string) => void; // fired after the user picks an option
}): { el: HTMLElement; getValue: () => string } {
  bindGlobalClose();
  let value = o.value || "";

  const dd = document.createElement("div");
  dd.className = "rp-dd";

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "rp-dd-trigger";
  const valSpan = document.createElement("span");
  valSpan.className = "rp-dd-val";
  trigger.appendChild(valSpan);
  trigger.insertAdjacentHTML("beforeend", `<svg class="rp-dd-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>`);
  dd.appendChild(trigger);

  const menu = document.createElement("div");
  menu.className = "rp-dd-menu";
  dd.appendChild(menu);

  // Search is ALWAYS there (Gery 2026-08-20): a dropdown you cannot search is the thing that
  // sends you scrolling. Callers can still opt out explicitly with searchable:false.
  let search: HTMLInputElement | null = null;
  if (o.searchable !== false) {
    search = document.createElement("input");
    search.className = "rp-dd-search";
    search.placeholder = "Search…";
    search.setAttribute("autocomplete", "off");
    menu.appendChild(search);
  }
  const list = document.createElement("div");
  list.className = "rp-dd-list";
  // height/scroll come from .rp-dd-list (~5 rows) — an inline maxHeight here used to override it
  menu.appendChild(list);

  const setLabel = (): void => {
    const lbl = o.options.find((x) => x.value === value)?.label || o.placeholder;
    valSpan.textContent = lbl;
    valSpan.classList.toggle("rp-dd-ph", !value);
  };

  function renderList(filter = ""): void {
    const f = filter.trim().toLowerCase();
    const matches = o.options.filter((opt) => !f || opt.label.toLowerCase().includes(f));
    list.innerHTML = "";
    if (!matches.length) {
      const empty = document.createElement("div");
      empty.className = "rp-dd-empty";
      empty.textContent = "No match";
      list.appendChild(empty);
      return;
    }
    // Show EVERY match in the scrollable list — no hard cap (#005b: the old slice(0,6)
    // made the picker unusable on mobile). The search field still narrows very long lists.
    for (const opt of matches) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "rp-dd-opt" + (opt.value === value ? " cur" : "");
      b.textContent = opt.label;
      b.addEventListener("click", () => { value = opt.value; setLabel(); dd.classList.remove("open"); o.onChange?.(value); });
      list.appendChild(b);
    }
  }

  trigger.addEventListener("click", () => {
    const willOpen = !dd.classList.contains("open");
    document.querySelectorAll(".rp-dd.open").forEach((x) => { if (x !== dd) x.classList.remove("open"); });
    dd.classList.toggle("open", willOpen);
    if (willOpen) {
      if (search) search.value = "";
      renderList();
      // Only steal focus when the list is long enough to warrant typing — on a phone an
      // autofocus pops the keyboard, which is pure annoyance for a 3-item list.
      if (search && o.options.length > 8) setTimeout(() => search!.focus(), 30);
    }
  });
  search?.addEventListener("input", () => renderList(search!.value));

  setLabel();
  renderList();

  return { el: dd, getValue: () => value };
}
